import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, CardActionArea } from '@mui/material';
import {
  Note as NoteIcon,
  Timeline as TimelineIcon,
  ViewList as ListIcon,
  Dashboard as DashboardIcon,
  GridView as CorkboardIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { getNotesListUrl, getCorkboardsUrl, getTimelinesUrl } from '../../../../../urls';

const StatCard = ({ title, value, icon: Icon, color = 'primary' }) => (
  <Card
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      p: 2,
      backgroundColor: `${color}.light`,
      color: `${color}.contrastText`,
    }}
  >
    <CardContent sx={{ textAlign: 'center' }}>
      <Icon sx={{ fontSize: 40, mb: 1 }} />
      <Typography variant="h4" component="div">
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
    </CardContent>
  </Card>
);

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string,
};

StatCard.defaultProps = {
  color: 'primary',
};

const ViewLink = ({ title, description, icon: Icon, color, onClick }) => (
  <Card
    sx={{
      height: '100%',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 6,
      },
    }}
  >
    <CardActionArea
      onClick={onClick}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        p: 2.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: 2,
          backgroundColor: `${color}.main`,
          color: 'white',
          mb: 2,
        }}
      >
        <Icon sx={{ fontSize: 28 }} />
      </Box>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
        {description}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: `${color}.main`,
          fontWeight: 500,
          fontSize: '0.875rem',
        }}
      >
        Open view <ArrowIcon sx={{ fontSize: 18, ml: 0.5 }} />
      </Box>
    </CardActionArea>
  </Card>
);

ViewLink.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

const NotesDashboard = ({ notes }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const stats = {
    total: notes.length,
    recent: notes.filter((note) => {
      const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return note.createdAt && note.createdAt > weekAgo;
    }).length,
    withTags: notes.filter((note) => note.tags && note.tags.length > 0).length,
    byType: notes.reduce((acc, note) => {
      const type = note.type || 'generic';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {}),
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        p: 3,
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Notes Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Notes" value={stats.total} icon={NoteIcon} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Recent (7 days)"
            value={stats.recent}
            icon={TimelineIcon}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="With Tags" value={stats.withTags} icon={ListIcon} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Types"
            value={Object.keys(stats.byType).length}
            icon={DashboardIcon}
            color="info"
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Note Views
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <ViewLink
              title="Corkboards"
              description="Infinite canvas boards with draggable notes and drawings. Great for visual brainstorming."
              icon={CorkboardIcon}
              color="warning"
              onClick={() => navigate(getCorkboardsUrl(projectId))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ViewLink
              title="Timelines"
              description="Visual timeline with draggable notes and chapter markers. Perfect for story chronology."
              icon={TimelineIcon}
              color="secondary"
              onClick={() => navigate(getTimelinesUrl(projectId))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ViewLink
              title="List View"
              description="Clean, sortable list of all your notes. Perfect for quick scanning and bulk management."
              icon={ListIcon}
              color="success"
              onClick={() => navigate(getNotesListUrl(projectId))}
            />
          </Grid>
        </Grid>
      </Box>

      {Object.keys(stats.byType).length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Notes by Type
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(stats.byType).map(([type, count]) => (
              <Grid item xs={12} sm={6} md={3} key={type}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                      {type}
                    </Typography>
                    <Typography variant="h6">{count}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {notes.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'text.secondary',
            gap: 2,
            mt: 4,
          }}
        >
          <Typography variant="h6">No notes yet</Typography>
          <Typography variant="body2">Create a note to start organizing your ideas.</Typography>
        </Box>
      )}
    </Box>
  );
};

NotesDashboard.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default NotesDashboard;
